/**
 * Convert parsed MIDI into a MusicPiece practice lesson,
 * splitting left / right hand by channel, track name, or pitch.
 */

import type { HandId, MusicPiece, NoteEvent } from "../music/types.js";
import type { ParsedMidiFile, RawMidiNote } from "./MidiFileParser.js";
import { parsedBpm } from "./MidiFileParser.js";

export type HandSplitMode = "auto" | "channel" | "pitch" | "track";

export interface MidiImportOptions {
  title?: string;
  /** MIDI pitch split (default Middle C = 60). Notes < split → left. */
  pitchSplit?: number;
  handSplit?: HandSplitMode;
  /** Cap huge files for practice UI. */
  maxNotes?: number;
}

function guessTimeSignature(
  parsed: ParsedMidiFile,
): MusicPiece["timeSignature"] {
  const key = `${parsed.numerator}/${parsed.denominator}`;
  if (key === "3/4" || key === "2/4" || key === "6/8" || key === "4/4") {
    return key;
  }
  return "4/4";
}

function handFromTrackName(name: string): HandId | null {
  const n = name.toLowerCase();
  if (/\bright\b|\br\.?\s*h\.?\b|\btreble\b|\bmelody\b/.test(n)) return "right";
  if (/\bleft\b|\bl\.?\s*h\.?\b|\bbass\b|\baccomp/.test(n)) return "left";
  return null;
}

function assignHand(
  note: RawMidiNote,
  mode: HandSplitMode,
  pitchSplit: number,
  trackHands: Map<number, HandId>,
): HandId {
  if (mode === "channel" || (mode === "auto" && note.channel > 0)) {
    if (note.channel === 0) return "right";
    if (note.channel === 1) return "left";
  }

  if (mode === "track" || mode === "auto") {
    const th = trackHands.get(note.trackIndex);
    if (th) return th;
  }

  if (mode === "pitch" || mode === "auto") {
    return note.midi >= pitchSplit ? "right" : "left";
  }

  return note.midi >= pitchSplit ? "right" : "left";
}

export function midiFileToMusicPiece(
  parsed: ParsedMidiFile,
  options: MidiImportOptions = {},
): MusicPiece {
  const pitchSplit = options.pitchSplit ?? 60;
  const mode = options.handSplit ?? "auto";
  const maxNotes = options.maxNotes ?? 4000;

  const trackHands = new Map<number, HandId>();
  parsed.trackNames.forEach((name, i) => {
    const h = handFromTrackName(name);
    if (h) trackHands.set(i, h);
  });

  const tpq = parsed.ticksPerQuarter || 480;
  const sliced = parsed.notes.slice(0, maxNotes);
  const notes: NoteEvent[] = sliced.map((n) => ({
    beat: n.tick / tpq,
    duration: Math.max(0.05, n.durationTicks / tpq),
    midi: n.midi,
    velocity: n.velocity,
    hand: assignHand(n, mode, pitchSplit, trackHands),
  }));

  const hands = new Set(notes.map((n) => n.hand));
  const hand: HandId =
    hands.has("right") && hands.has("left")
      ? "both"
      : hands.has("left")
        ? "left"
        : "right";

  const bpm = Math.max(40, Math.min(240, parsedBpm(parsed)));

  return {
    id: `import-${Date.now()}`,
    title: options.title ?? "Imported MIDI",
    category: "melody",
    bpm,
    timeSignature: guessTimeSignature(parsed),
    key: "imported",
    hand,
    stage: 0,
    description: `Imported MIDI · ${notes.length} notes · ${parsed.trackCount} track(s) · split=${mode}`,
    notes,
  };
}

function writeVarLen(track: number[], value: number): void {
  const stack: number[] = [];
  stack.push(value & 0x7f);
  let v = value >> 7;
  while (v > 0) {
    stack.push((v & 0x7f) | 0x80);
    v >>= 7;
  }
  for (let i = stack.length - 1; i >= 0; i--) track.push(stack[i]);
}

/**
 * Build a tiny valid Type-0 MIDI (C–E–G–C) for automated tests.
 */
export function buildTestMidiBytes(): Uint8Array {
  const tpq = 480;
  const track: number[] = [];

  writeVarLen(track, 0);
  track.push(0xff, 0x51, 0x03, 0x07, 0xa1, 0x20); // tempo 500000
  writeVarLen(track, 0);
  track.push(0xff, 0x58, 0x04, 0x04, 0x02, 0x18, 0x08); // 4/4

  const name = "Right Hand";
  writeVarLen(track, 0);
  track.push(0xff, 0x03, name.length);
  for (let i = 0; i < name.length; i++) track.push(name.charCodeAt(i));

  const seq = [60, 64, 67, 72];
  for (let i = 0; i < seq.length; i++) {
    writeVarLen(track, 0);
    track.push(0x90, seq[i], 100);
    writeVarLen(track, tpq);
    track.push(0x80, seq[i], 0);
  }

  writeVarLen(track, 0);
  track.push(0xff, 0x2f, 0x00);

  const out = new Uint8Array(14 + 8 + track.length);
  const dv = new DataView(out.buffer);
  out[0] = 0x4d;
  out[1] = 0x54;
  out[2] = 0x68;
  out[3] = 0x64;
  dv.setUint32(4, 6);
  dv.setUint16(8, 0);
  dv.setUint16(10, 1);
  dv.setUint16(12, tpq);
  let o = 14;
  out[o++] = 0x4d;
  out[o++] = 0x54;
  out[o++] = 0x72;
  out[o++] = 0x6b;
  dv.setUint32(o, track.length);
  o += 4;
  out.set(track, o);
  return out;
}
